import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAdminStore } from '../lib/store';
import clsx from 'clsx';
import { IconPlus, IconEye, IconEdit, IconClipboard, IconCalendar } from '../components/Icons';
import { card, cardHead, th, td, thMd, tdMd, thSm, tdSm, trHover, iconBtn, btnPrimary, btnSecondary, btnSm, StatusChip, EmptyState, Badge, SegmentedControl } from '../components/ui';

interface CategoryStats {
  [category: string]: number;
}

interface EventStatsData {
  eventId: string;
  categoryDistribution: CategoryStats;
  loading: boolean;
  error?: string;
}

type PaymentFilter = 'all' | 'pending' | 'verified';

// ---------- small presentational pieces ----------

const linkBtn = 'text-[13px] font-medium text-primary hover:text-primary-dark';

function Kpi({ label, value, note, tone = 'default' }: {
  label: string;
  value: string | number;
  note?: string;
  tone?: 'default' | 'warn' | 'ok';
}) {
  return (
    <div className="px-4 py-4 sm:px-6 sm:py-5 flex flex-col gap-1.5 min-w-0 border-line-soft odd:border-l-0 even:border-l [&:nth-child(n+3)]:border-t xl:[&:nth-child(n+2)]:border-l xl:[&:nth-child(n+3)]:border-t-0">
      <span className="text-[13px] text-muted">{label}</span>
      <span className={clsx(
        'tabular text-2xl sm:text-[30px] font-bold leading-none tracking-tight break-all',
        tone === 'warn' && 'text-warn',
      )}>
        {value}
      </span>
      {note && (
        <span className={clsx('text-[13px]', tone === 'ok' ? 'text-ok' : 'text-muted')}>{note}</span>
      )}
    </div>
  );
}

// Simple bar chart for category distribution
function CategoryChart({ data, loading, error }: { data: CategoryStats; loading: boolean; error?: string }) {
  if (loading) {
    return <div className="text-center text-muted py-4 text-sm animate-pulse">統計資料載入中</div>;
  }
  if (error) {
    return <div className="text-center text-bad py-4 text-sm">載入失敗：{error}</div>;
  }
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <div className="text-center text-muted py-4 text-sm">尚無報名資料</div>;
  }
  const maxCount = Math.max(...entries.map(([, count]) => count));
  return (
    <div className="flex flex-col gap-3">
      {entries.map(([category, count]) => (
        <div key={category} className="flex items-center gap-3">
          <div className="w-24 text-[13px] text-muted truncate" title={category}>{category}</div>
          <div className="flex-1 bg-line-soft rounded-full h-1.5 overflow-hidden">
            <div className="h-full rounded-full bg-primary" style={{ width: `${(count / maxCount) * 100}%` }} />
          </div>
          <div className="tabular w-8 text-[13px] font-medium text-right">{count}</div>
        </div>
      ))}
    </div>
  );
}

// ---------- helpers ----------

function registrantName(reg: any) {
  return reg.formData?.name || reg.formData?.p1_name || reg.userDisplayName || '未填寫';
}

function formatTimeAgo(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return '剛剛';
  if (diffMins < 60) return `${diffMins} 分鐘前`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} 小時前`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} 天前`;
}

function formatWaiting(dateStr: string) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `等待 ${Math.max(mins, 1)} 分鐘`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `等待 ${hours} 小時`;
  return `等待 ${Math.floor(hours / 24)} 天`;
}

function formatToday() {
  return new Intl.DateTimeFormat('zh-TW', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }).format(new Date());
}

function formatNtd(n: number) {
  return `NT$ ${n.toLocaleString('zh-TW')}`;
}

// ---------- page ----------

export default function Dashboard() {
  const { events, setEvents, stats, setStats } = useAdminStore();
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventStats, setEventStats] = useState<Record<string, EventStatsData>>({});
  const [filter, setFilter] = useState<PaymentFilter>('all');

  useEffect(() => {
    async function loadData() {
      try {
        const [eventsRes, regsRes] = await Promise.all([
          api.getEvents(),
          api.getRegistrations(),
        ]);

        if (eventsRes.success) {
          setEvents(eventsRes.data);
        }

        if (regsRes.success) {
          setRegistrations(regsRes.data);
          setStats({
            activeEvents: eventsRes.data.filter((e: any) => e.status === 'published').length,
            totalRegistrations: regsRes.data.length,
            pendingPayments: regsRes.data.filter((r: any) => r.paymentStatus === 'pending').length,
            totalRevenue: regsRes.data
              .filter((r: any) => r.paymentStatus === 'verified')
              .reduce((sum: number, r: any) => sum + (r.totalAmount || 0), 0),
          });
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [setEvents, setStats]);

  // Fetch stats for published events
  useEffect(() => {
    const publishedEvents = events.filter(e => e.status === 'published').slice(0, 2);

    publishedEvents.forEach(async (event) => {
      if (eventStats[event.id] && !eventStats[event.id].loading) {
        return;
      }

      setEventStats(prev => ({
        ...prev,
        [event.id]: { eventId: event.id, categoryDistribution: {}, loading: true }
      }));

      try {
        const res = await api.getEventStats(event.id);
        if (res.success) {
          setEventStats(prev => ({
            ...prev,
            [event.id]: {
              eventId: event.id,
              categoryDistribution: res.data.categoryDistribution || {},
              loading: false
            }
          }));
        }
      } catch (error) {
        console.error(`Failed to load stats for event ${event.id}:`, error);
        setEventStats(prev => ({
          ...prev,
          [event.id]: { eventId: event.id, categoryDistribution: {}, loading: false, error: '載入失敗' }
        }));
      }
    });
  }, [events]);

  const recent = useMemo(() => {
    const list = filter === 'all' ? registrations : registrations.filter(r => r.paymentStatus === filter);
    return list.slice(0, 5);
  }, [registrations, filter]);

  const pendingQueue = useMemo(() => {
    return registrations
      .filter(r => r.paymentStatus === 'pending')
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .slice(0, 4);
  }, [registrations]);

  const pendingAmount = useMemo(
    () => registrations.filter(r => r.paymentStatus === 'pending').reduce((s, r) => s + (r.totalAmount || 0), 0),
    [registrations],
  );

  const oldestPending = pendingQueue[0];
  const publishedEvents = events.filter(e => e.status === 'published').slice(0, 2);

  return (
    <div className="px-4 py-5 sm:px-6 sm:py-7 md:px-10 md:py-8 flex flex-col gap-5 sm:gap-7 [&>*]:min-w-0">
      {/* Page header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl sm:text-2xl font-bold leading-tight">總覽</h1>
          <p className="text-sm text-muted">{formatToday()}</p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <Link to="/events/new" className={btnPrimary}>
            <IconPlus size={16} />
            新增活動
          </Link>
        </div>
      </div>

      {/* KPI band */}
      <section aria-label="重點數字" className={clsx(card, 'grid grid-cols-2 xl:grid-cols-4 overflow-hidden')}>
        <Kpi
          label="進行中活動"
          value={stats.activeEvents}
          note={events.length > 0 ? `共 ${events.length} 場活動` : undefined}
        />
        <Kpi
          label="總報名人數"
          value={stats.totalRegistrations.toLocaleString('zh-TW')}
          note={stats.totalRegistrations > 0 ? `${stats.totalRegistrations.toLocaleString('zh-TW')} 筆報名` : undefined}
        />
        <Kpi
          label="待審核付款"
          value={stats.pendingPayments}
          tone={stats.pendingPayments > 0 ? 'warn' : 'default'}
          note={oldestPending ? `最久已${formatWaiting(oldestPending.createdAt)}` : '目前沒有待審核'}
        />
        <Kpi
          label="已收款金額"
          value={formatNtd(stats.totalRevenue)}
          note={pendingAmount > 0 ? `尚有 ${formatNtd(pendingAmount)} 待確認` : undefined}
        />
      </section>

      {/* Registrations + queue */}
      <div className="flex flex-wrap gap-5 sm:gap-6 items-start">
        {/* Recent registrations */}
        <section aria-label="最近報名" className={clsx(card, 'flex-[999_1_560px] min-w-0 flex flex-col')}>
          <div className={cardHead}>
            <h2 className="text-base font-bold">最近報名</h2>
            <SegmentedControl
              ariaLabel="篩選狀態"
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: '全部' },
                { value: 'pending', label: '待審核' },
                { value: 'verified', label: '已確認' },
              ]}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={th}>報名者</th>
                  <th className={thMd}>活動</th>
                  <th className={thMd}>組別</th>
                  <th className={th}>狀態</th>
                  <th className={th}>時間</th>
                  <th className={thSm}><span className="sr-only">操作</span></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-muted text-sm">載入中</td></tr>
                ) : recent.length === 0 ? (
                  <tr><td colSpan={6} className="p-0"><EmptyState
                    icon={<IconClipboard size={28} />}
                    title={filter === 'all' ? '尚無報名資料' : '沒有符合的報名'}
                    hint={filter === 'all' ? '報名者透過 LINE 送出表單後會顯示在這裡' : undefined}
                  /></td></tr>
                ) : recent.map((reg) => (
                  <tr key={reg.id} className={trHover}>
                    <td className={td}>
                      <div className="flex flex-col leading-snug">
                        <span className="font-medium">{registrantName(reg)}</span>
                        <span className="text-xs text-muted">{reg.userDisplayName || reg.formData?.line_name || '-'}</span>
                        <span className="md:hidden text-xs text-muted truncate max-w-[140px]">{reg.eventTitle || reg.eventId}</span>
                      </div>
                    </td>
                    <td className={tdMd}>{reg.eventTitle || reg.eventId}</td>
                    <td className={tdMd}>{reg.selectedCategoryId || '-'}</td>
                    <td className={td}><StatusChip status={reg.paymentStatus} /></td>
                    <td className={clsx(td, 'tabular text-muted')}>{formatTimeAgo(reg.createdAt)}</td>
                    <td className={clsx(tdSm, 'text-right')}>
                      <Link to={`/registrations/${reg.eventId}`} className={linkBtn}>查看</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-5 py-3 border-t border-line-soft text-[13px] text-muted">
            <span className="tabular">
              {loading ? '' : `顯示最近 ${recent.length} 筆，共 ${registrations.length.toLocaleString('zh-TW')} 筆`}
            </span>
            <Link to="/registrations" className={linkBtn}>前往報名列表</Link>
          </div>
        </section>

        {/* Right column */}
        <div className="flex-[1_1_320px] min-w-0 flex flex-col gap-5 sm:gap-6">
          {/* Pending payments queue */}
          <section aria-label="待處理付款" className={clsx(card, 'flex flex-col')}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-line-soft">
              <h2 className="text-base font-bold">待處理付款</h2>
              {stats.pendingPayments > 0 && (
                <Badge tone="warn" className="tabular">{stats.pendingPayments} 筆</Badge>
              )}
            </div>
            {loading ? (
              <div className="px-5 py-8 text-center text-muted text-sm">載入中</div>
            ) : pendingQueue.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <div className="text-sm font-medium">沒有待審核的付款</div>
                <div className="text-[13px] text-muted mt-1">新的匯款回報會出現在這裡</div>
              </div>
            ) : (
              <ul className="flex flex-col">
                {pendingQueue.map((reg, i) => {
                  const waitedDays = (Date.now() - new Date(reg.createdAt).getTime()) / 86400000;
                  return (
                    <li
                      key={reg.id}
                      className={clsx('flex items-center gap-3 px-5 py-3.5', i < pendingQueue.length - 1 && 'border-b border-line-soft')}
                    >
                      <div className="flex-1 min-w-0 flex flex-col leading-snug gap-0.5">
                        <span className="text-sm font-medium truncate">{registrantName(reg)}</span>
                        <span className="text-xs text-muted truncate">{reg.eventTitle || reg.eventId}</span>
                      </div>
                      <div className="flex flex-col items-end leading-snug gap-0.5">
                        <span className="tabular text-sm font-medium">{formatNtd(reg.totalAmount || 0)}</span>
                        <span className={clsx('tabular text-xs', waitedDays >= 2 ? 'text-bad' : 'text-muted')}>
                          {formatWaiting(reg.createdAt)}
                        </span>
                      </div>
                      <Link
                        to={`/registrations/${reg.eventId}`}
                        className={clsx(btnSecondary, btnSm, 'text-primary shrink-0')}
                      >
                        審核
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
            {stats.pendingPayments > pendingQueue.length && (
              <div className="px-5 py-3 border-t border-line-soft">
                <Link to="/registrations" className={linkBtn}>查看全部 {stats.pendingPayments} 筆待審核</Link>
              </div>
            )}
          </section>

          {/* Category distribution for published events */}
          {publishedEvents.map((event) => {
            const s = eventStats[event.id] || { categoryDistribution: {}, loading: true };
            return (
              <section key={event.id} aria-label={`${event.title} 組別分布`} className={card}>
                <div className="px-5 py-4 border-b border-line-soft">
                  <h2 className="text-sm font-bold truncate" title={event.title}>{event.title}</h2>
                  <p className="text-xs text-muted mt-0.5">組別分布</p>
                </div>
                <div className="p-5">
                  <CategoryChart data={s.categoryDistribution} loading={s.loading} error={s.error} />
                </div>
              </section>
            );
          })}
        </div>
      </div>

      {/* Events list */}
      <section aria-label="活動列表" className={clsx(card, 'flex flex-col')}>
        <div className={cardHead}>
          <h2 className="text-base font-bold">活動列表</h2>
          <Link to="/events" className={clsx(btnSecondary, btnSm)}>
            管理所有活動
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={th}>活動名稱</th>
                <th className={thMd}>活動日期</th>
                <th className={thMd}>報名截止</th>
                <th className={th}>報名人數</th>
                <th className={thSm}>狀態</th>
                <th className={th}><span className="sr-only">操作</span></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-muted text-sm">載入中</td></tr>
              ) : events.length === 0 ? (
                <tr><td colSpan={6} className="p-0"><EmptyState
                  icon={<IconCalendar size={28} />}
                  title="尚無活動"
                  hint="點「新增活動」建立第一場活動"
                /></td></tr>
              ) : events.map((event) => (
                <tr key={event.id} className={trHover}>
                  <td className={td}>
                    <div className="flex flex-col leading-snug">
                      <span className="font-medium">{event.title}</span>
                      {event.titleEn && <span className="text-xs text-muted">{event.titleEn}</span>}
                      <StatusChip status={event.status} className="sm:hidden mt-1 self-start" />
                    </div>
                  </td>
                  <td className={clsx(tdMd, 'tabular text-muted')}>{event.eventDate || '-'}</td>
                  <td className={clsx(tdMd, 'tabular text-muted')}>{event.registrationDeadline?.split('T')[0] || '尚未設定'}</td>
                  <td className={td}>
                    <span className="tabular font-medium">{event.bookingCount || 0} 人</span>
                    {(event.confirmedCount ?? 0) > 0 && (
                      <span className="tabular text-xs text-ok block sm:inline sm:ml-2">{event.confirmedCount} 已確認</span>
                    )}
                  </td>
                  <td className={tdSm}><StatusChip status={event.status} /></td>
                  <td className={clsx(td, 'text-right')}>
                    <div className="inline-flex gap-1.5">
                      <Link
                        to={`/registrations/${event.id}`}
                        aria-label={`查看 ${event.title} 的報名`}
                        title="查看報名"
                        className={iconBtn}
                      >
                        <IconEye size={16} />
                      </Link>
                      <Link
                        to={`/events/${event.id}/edit`}
                        aria-label={`編輯 ${event.title}`}
                        title="編輯"
                        className={iconBtn}
                      >
                        <IconEdit size={16} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
